/**
 * Presentation Store
 * Manages state for the presentation phases (PRESENTER_INTRO and PRESENTING)
 */

import { writable, derived, get } from 'svelte/store';
import type { Presentation } from '@blather/shared';
import { gameStore } from './gameStore';
import { playerStore } from './playerStore';

interface PresentationState {
  currentPresentation: Presentation | null;
  isPresenter: boolean;
  hasGuessedCorrectly: boolean;
  remainingTime: number; // milliseconds
  lastGuessResult: { isCorrect: boolean; correctAnswer: string | null } | null;
}

function createPresentationStore() {
  const { subscribe, set, update } = writable<PresentationState>({
    currentPresentation: null,
    isPresenter: false,
    hasGuessedCorrectly: false,
    remainingTime: 0,
    lastGuessResult: null,
  });

  // Sync with game store
  gameStore.subscribe(($gameStore) => {
    const presentation = $gameStore.gameState?.currentPresentation;
    const playerId = get(playerStore).player?.id;
    const phase = $gameStore.gameState?.phase;
    const timerEndTime = $gameStore.gameState?.timerEndTime;

    update((state) => {
      // Calculate remaining time from timerEndTime
      const remainingTime = timerEndTime ? Math.max(0, timerEndTime - Date.now()) : 0;

      return {
        ...state,
        currentPresentation: presentation || null,
        isPresenter: presentation?.playerId === playerId,
        hasGuessedCorrectly:
          presentation?.correctGuessers.includes(playerId || '') || false,
        remainingTime,
      };
    });
  });

  return {
    subscribe,

    setGuessResult(result: {
      isCorrect: boolean;
      correctAnswer: string | null;
    }) {
      update((state) => ({ ...state, lastGuessResult: result }));
    },

    updateRemainingTime(timeMs: number) {
      update((state) => ({ ...state, remainingTime: timeMs }));
    },

    clearGuessResult() {
      update((state) => ({ ...state, lastGuessResult: null }));
    },

    reset() {
      set({
        currentPresentation: null,
        isPresenter: false,
        hasGuessedCorrectly: false,
        remainingTime: 0,
        lastGuessResult: null,
      });
    },
  };
}

export const presentationStore = createPresentationStore();

// Derived stores
export const formattedTime = derived(presentationStore, ($store) => {
  const totalSeconds = Math.ceil($store.remainingTime / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

export const presentationClues = derived(
  presentationStore,
  ($store) => $store.currentPresentation?.clues || []
);
